import '@/styles/globals.css';
import 'nprogress/nprogress.css';
import { useEffect } from 'react';
import Head from 'next/head';
import { NextIntlClientProvider } from 'next-intl';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import NProgress from 'nprogress';

NProgress.configure({ showSpinner: false });

type PageProps = {
  messages: IntlMessages;
  now: number;
  seo?: {
    title: string;
    description: string;
    keywords: string;
    ogLocale: string;
  };
};

type Props = Omit<AppProps<PageProps>, 'pageProps'> & {
  pageProps: PageProps;
};

export default function App({ Component, pageProps }: Props) {
  const router = useRouter();
  const siteUrl = 'https://loom.vegamo.cn';
  const locale = router.locale ?? 'zh';
  const routePath = router.asPath.split('?')[0]?.split('#')[0] ?? '/';
  const canonical = `${siteUrl}${routePath === '/' ? '' : routePath}`;
  const seo = pageProps.seo ?? {
    title: 'Loom - 用自然语言生成 API Schema 与文档的 AI Agent',
    description:
      'Loom 是面向开发者的开源 AI Agent，可从自然语言生成 JSON Schema API、可复用数据模型、AI API 文档、接口说明与 Mock 服务，帮助团队更快完成 API 设计、联调与交付。',
    keywords:
      'AI API 生成器, AI 文档生成, API 文档生成, API 文档工具, API Schema 生成, JSON Schema 生成器, 接口文档, 接口说明生成, Mock Server, 接口设计, 自然语言生成接口, 开发者 AI 工具, 后端开发工具, Schema 驱动开发',
    ogLocale: 'zh_CN',
  };

  useEffect(() => {
    const handleStart = () => {
      NProgress.start();
    };

    const handleStop = () => {
      NProgress.done();
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  return (
    <>
      <Head>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Loom" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={seo.ogLocale} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="zh" href={locale === 'zh' ? canonical : `${siteUrl}${routePath.replace(/^\/(en|de)/, '') || '/'}`} />
        <link rel="alternate" hrefLang="en" href={`${siteUrl}/en${routePath === '/' ? '' : routePath.replace(/^\/(en|de)/, '')}`} />
        <link rel="alternate" hrefLang="de" href={`${siteUrl}/de${routePath === '/' ? '' : routePath.replace(/^\/(en|de)/, '')}`} />
        <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${routePath.replace(/^\/(en|de)/, '') || '/'}`} />
      </Head>
      <NextIntlClientProvider
        // To achieve consistent date, time and number formatting
        // across the app, you can define a set of global formats.
        formats={{
          dateTime: {
            short: {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            },
          },
        }}
        messages={pageProps.messages}
        // Providing an explicit value for `now` ensures consistent formatting of
        // relative values regardless of the server or client environment.
        now={new Date(pageProps.now)}
        // Also an explicit time zone is helpful to ensure dates render the
        // same way on the client as on the server, which might be located
        // in a different time zone.
        timeZone="Austria/Vienna"
        locale={locale}
      >
        <Component {...pageProps} />
      </NextIntlClientProvider>
    </>
  );
}
